// src/pages/grow-business/reschedule/[id].tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { SchedulerProvider, useScheduler } from '@/components/scheduler/SchedulerContext';
import { CalendarView } from '@/components/scheduler/CalendarView';
import { TimeSlotPicker } from '@/components/scheduler/TimeSlotPicker';
import { AppointmentDetails, AvailabilityResponse } from '@/components/scheduler/types';

// Inner component that uses the scheduler context
const RescheduleForm: React.FC<{
    appointment: AppointmentDetails;
    appointmentId: string;
}> = ({ appointment, appointmentId }) => {
    const navigate = useNavigate();
    const { apiBaseUrl } = useScheduler();
    const [loading, setLoading] = useState(true);
    const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [timezone, setTimezone] = useState<string>('');
    const [rescheduling, setRescheduling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchAvailability();
        detectTimezone();
    }, []);

    const detectTimezone = () => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTimezone(tz);
    };

    const fetchAvailability = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${apiBaseUrl}/api/availability/slots`);
            if (!response.ok) throw new Error('Failed to fetch availability');

            const data: AvailabilityResponse = await response.json();
            setAvailability(data);

            // Auto-select first available date
            const dates = Object.keys(data.availability);
            if (dates.length > 0) {
                setSelectedDate(dates[0]);
            }
        } catch (err) {
            setError('Unable to load available times. Please try again.');
            console.error('Error fetching availability:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleReschedule = async () => {
        if (!selectedDate || !selectedTime) return;

        setRescheduling(true);
        setError(null);

        try {
            const response = await fetch(`${apiBaseUrl}/api/booking/reschedule`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    appointment_id: appointmentId,
                    new_date: selectedDate,
                    new_time: selectedTime
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to reschedule');
            }

            // Show success message and redirect
            navigate(`/grow-business/rescheduled/${appointmentId}`, {
                state: {
                    success: true,
                    newDate: selectedDate,
                    newTime: selectedTime
                }
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to reschedule appointment. Please try again.');
        } finally {
            setRescheduling(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e2ba57]"></div>
            </div>
        );
    }

    if (!availability || Object.keys(availability.availability).length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No available time slots in the next few days.</p>
                <button
                    onClick={fetchAvailability}
                    className="text-[#e2ba57] hover:text-[#d1a43f] font-medium"
                >
                    Check again
                </button>
            </div>
        );
    }

    const availableDates = Object.keys(availability.availability);
    const currentDateSlots = selectedDate ? availability.availability[selectedDate]?.slots || [] : [];

    return (
        <div className="space-y-6">
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800">
                    <strong>Current appointment:</strong>{' '}
                    {new Date(appointment.appointment_time).toLocaleString()}
                </p>
            </div>

            <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-[#212A37] mb-2">
                    Select New Time
                </h3>
                <p className="text-gray-600">
                    All times shown in {timezone || availability.timezone}
                </p>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
                {/* Calendar */}
                <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Select Date</h4>
                    <CalendarView
                        availableDates={availableDates}
                        selectedDate={selectedDate}
                        onDateSelect={setSelectedDate}
                        availability={availability.availability}
                    />
                </div>

                {/* Time Slots */}
                <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Available Times {selectedDate && availability.availability[selectedDate] && (
                            <span className="text-gray-500 font-normal">
                                - {availability.availability[selectedDate].formattedDate}
                            </span>
                        )}
                    </h4>
                    {selectedDate && currentDateSlots.length > 0 ? (
                        <TimeSlotPicker
                            slots={currentDateSlots}
                            selectedTime={selectedTime}
                            onTimeSelect={setSelectedTime}
                        />
                    ) : (
                        <p className="text-gray-500 text-sm">Select a date to see available times</p>
                    )}
                </div>
            </div>

            {/* Reschedule Button */}
            {selectedTime && (
                <div className="text-center pt-6">
                    <button
                        onClick={handleReschedule}
                        disabled={rescheduling}
                        className={`px-8 py-3 rounded-md font-semibold transition-all ${rescheduling
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-[#e2ba57] text-[#212A37] hover:bg-[#d1a43f]'
                            }`}
                    >
                        {rescheduling ? 'Rescheduling...' : 'Confirm New Time'}
                    </button>
                </div>
            )}
        </div>
    );
};

// Main reschedule page component
const ReschedulePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [appointment, setAppointment] = useState<AppointmentDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';

    useEffect(() => {
        if (id) {
            fetchAppointment();
        }
    }, [id]);

    const fetchAppointment = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/api/booking/${id}`);
            if (!response.ok) {
                throw new Error('Appointment not found');
            }
            const data: AppointmentDetails = await response.json();

            // Check if appointment is already cancelled
            if (data.status === 'cancelled') {
                setError('This appointment has already been cancelled.');
            } else {
                setAppointment(data);
            }
        } catch (err) {
            setError('Unable to find appointment. Please check your link and try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e2ba57]"></div>
            </div>
        );
    }

    if (error || !appointment) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
                    <h1 className="text-2xl font-bold text-[#212A37] mb-4">Unable to Reschedule</h1>
                    <p className="text-gray-600 mb-6">{error || 'Appointment not found'}</p>
                    <a
                        href="/grow-business"
                        className="inline-block bg-[#e2ba57] text-[#212A37] px-6 py-2 rounded-md font-semibold hover:bg-[#d1a43f] transition-colors"
                    >
                        Schedule New Appointment
                    </a>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Reschedule Appointment | Swift Lead Digital</title>
            </Helmet>

            <SchedulerProvider apiBaseUrl={apiBaseUrl}>
                <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="bg-[#212A37] text-white px-6 py-4">
                                <h1 className="text-2xl font-bold">Reschedule Your Appointment</h1>
                                <p className="text-gray-300 mt-1">
                                    Appointment with {appointment.customer_name}
                                </p>
                            </div>

                            <div className="p-6 md:p-8">
                                <RescheduleForm
                                    appointment={appointment}
                                    appointmentId={id!}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </SchedulerProvider>
        </>
    );
};

export default ReschedulePage;